import SidebarNavItem from "@/shared/ui/sidebarNavItem/SidebarNavItem";
import styles from "./Sidebar.module.scss";
import { type FC, useState } from "react";
import SkadLogo from "@/widgets/sidebar/assets/skadLogo.svg";
import SkadLogoText from "@/widgets/sidebar/assets/logoWithText.png";
import UsersIcon from "@/shared/assets/usersIcon.svg";
import NomenclatureIcon from "@/shared/assets/nomenclatureIcon.svg";
import RemainsIcon from "@/shared/assets/remainsIcon.svg";
import OrdersIcon from "@/shared/assets/ordersIcon.svg";
import ReportsIcon from "@/shared/assets/reportsIcon.svg";
import SettingsIcon from "@/shared/assets/settingsIcon.svg";
import SwitchIcon from "@/widgets/sidebar/assets/switchIcon.svg";
import FAQIcon from "@/shared/assets/faqIcon.svg";
import Logout from "@/shared/assets/logout.svg";
import { type ReactNode } from "react";
import { getDeadline, InfoMessageBar, PATHNAMES, UserAvatar } from "@/shared";
import { useAppDispatch, useAppSelector } from "@/shared/hooks";
import { useLocation, useNavigate } from "react-router-dom";
import { logout } from "@/app/model/applicationSlice";
import { revertAll } from "@/app/model/actions";
import { CREATE_PROD_ORDER_DEADLINE } from "@/shared/constants/otherConst";
import clsx from "clsx";
import type { UserRolesType } from "@/app/model/types";
import dayjs from "dayjs";
import "dayjs/locale/ru";

dayjs.locale("ru");

interface SidebarProps {
  isSidebarSmall: boolean;
  setIsSidebarSmall: (value: boolean) => void;
}

export type SidebarItemsType = {
  name: string;
  href: string | null;
  iconPath?: string;
  iconComponent?: ReactNode | null;
  hideForRoles: UserRolesType[];
  id?: string;
  parentId?: string;
};

const {
  loginPath,
  nomenclaturePath,
  ordersPath,
  remainsPath,
  reportsPath,
  usersPath,
  settingsPath,
  faqPath,
} = PATHNAMES;

export const Sidebar: FC<SidebarProps> = ({
  isSidebarSmall,
  setIsSidebarSmall,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const [parentIds, setParentIds] = useState<string[]>([]);

  const { fio, roleName, isDealerUser, image, dealerProlongationDate } =
    useAppSelector((state) => state.viewer);

  const SidebarItems: SidebarItemsType[] = [
    {
      name: "Дилер",
      href: usersPath,
      iconComponent: <UsersIcon />,
      hideForRoles: [],
    },
    {
      name: "Номенклатура",
      href: nomenclaturePath,
      iconComponent: <NomenclatureIcon />,
      hideForRoles: ["Marketer"],
    },
    {
      name: "Заказы",
      href: ordersPath,
      iconComponent: <OrdersIcon />,
      hideForRoles: ["Marketer"],
    },
    {
      name: "Остатки",
      href: remainsPath,
      iconComponent: <RemainsIcon />,
      hideForRoles: ["Marketer"],
    },
    {
      name: "Отчетность",
      href: reportsPath,
      iconComponent: <ReportsIcon />,
      hideForRoles: [],
    },
    {
      name: "Настройки",
      href: settingsPath,
      iconComponent: <SettingsIcon />,
      hideForRoles: ["Sales", "Marketer"],
    },
  ];

  const skadTabs: SidebarItemsType[] = [
    {
      name: "Справка",
      href: null,
      iconComponent: <FAQIcon />,
      hideForRoles: ["Marketer"],
      id: "faq",
    },
    {
      name: "Для дилера",
      href: faqPath.dealer,
      iconComponent: null,
      hideForRoles: ["Dealer", "Sales", "Marketer"],
      parentId: "faq",
    },
    {
      name: "Для СКАД",
      href: faqPath.manager,
      iconComponent: null,
      hideForRoles: ["Dealer", "Sales", "Marketer"],
      parentId: "faq",
    },
  ];
  const dealerTabs: SidebarItemsType[] = [
    {
      name: "Справка",
      href: faqPath.dealer,
      iconComponent: <FAQIcon />,
      hideForRoles: ["Admin", "Manager", "Marketer"],
    },
  ];

  const newSideBarItems = isDealerUser
    ? [...SidebarItems, ...dealerTabs]
    : [...SidebarItems, ...skadTabs];
  const name = fio
    ? `${fio.split(" ")[0]} ${fio.split(" ")[1]}`
    : "Firstname Lastname";

  const handleClick = () => {
    if (isSidebarSmall) {
      localStorage.removeItem("sidebar-mini");
    } else {
      localStorage.setItem("sidebar-mini", "1");
    }
    setTimeout(() => {
      setIsSidebarSmall(!isSidebarSmall);
    }, 1);
  };

  const childElementsToggler = (id: string) => {
    if (parentIds.includes(id)) {
      const newArr = [...parentIds];
      const filtered = newArr.filter((i) => i !== id);
      setParentIds(filtered);
    } else {
      setParentIds((prev) => [...prev, id]);
    }
  };

  const exitClick = () => {
    dispatch(logout());
    dispatch(revertAll());

    navigate(loginPath);
  };

  return (
    <aside className={clsx(styles.sidebar, { [styles.mini]: isSidebarSmall })}>
      <div className={styles.top}>
        <div className={styles.title}>
          <div className={styles.logo}>
            {isSidebarSmall ? (
              <SkadLogo fill="url(#pattern0)" width={50} height={50} />
            ) : (
              <img src={SkadLogoText} alt="logo" />
            )}
          </div>
        </div>
        {newSideBarItems.map((item) => {
          if (!item.hideForRoles.includes(roleName)) {
            return (
              <SidebarNavItem
                key={item.name}
                item={item}
                childElementsToggler={childElementsToggler}
                parentIds={parentIds}
                isSidebarSmall={isSidebarSmall}
              />
            );
          }
        })}
      </div>
      <div>
        {isDealerUser && pathname === nomenclaturePath && (
          <div className={styles.infoBar}>
            <InfoMessageBar
              type="aside"
              text={
                isSidebarSmall
                  ? "Внимание"
                  : `Внимание! До ${dealerProlongationDate ? dayjs(dealerProlongationDate).subtract(1, "day").format("DD MMMM") : CREATE_PROD_ORDER_DEADLINE} ${
                      dealerProlongationDate == null
                        ? getDeadline(CREATE_PROD_ORDER_DEADLINE, true)
                        : ""
                    } открыта возможность формирования заказа на производство. Вы можете оформить заказ из журнала номенклатуры или загрузить бланк заказа.`
              }
            />
          </div>
        )}
        <div className={styles.bottom}>
          <div className={styles.bottomInfo}>
            <div className={styles.bottomAvatar}>
              <UserAvatar fio={fio} image={image} />
            </div>
            <div className={styles.bottomMid}>
              <div className={styles.userName}>{name}</div>
              <div className={styles.bottomLogout}>
                <div className={styles.bottomImg}>
                  <Logout />
                </div>
                <div className={styles.bottomLink} onClick={exitClick}>
                  Выйти
                </div>
              </div>
            </div>
            <div className={styles.switchBar}>
              <SwitchIcon onClick={handleClick} />
            </div>
          </div>
          <div className={styles.bottomVersion}>{__VERSION__}</div>
        </div>
      </div>
    </aside>
  );
};
